import { ConnectorHosting } from "@p0security/integrations-app-shared/components";
import {
  newVaultElement,
  withoutAzureKeyVault,
} from "@p0security/integrations-vaulted-credential-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

// A connector's id names its Terraform resources, and each tailnet picks its
// connector by it. The pattern and its 63-character limit are a DNS label's
// (RFC 1123), as Kubernetes names an object.
const CONNECTOR_ID_PATTERN = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;
const CONNECTOR_ID_MAX_LENGTH = 63;

export const INVALID_CONNECTOR_ID = `Use lowercase letters, digits and hyphens only, starting and ending with a letter or digit, up to ${CONNECTOR_ID_MAX_LENGTH} characters.`;

/** Where an OAuth client's secret is vaulted: the customer's own AWS Secrets
 * Manager or Google Secret Manager, in the same cloud as the tailnet's
 * connector. P0 stores where the secret is, never the secret. */
export const TailscaleSecretVault = withoutAzureKeyVault(
  newVaultElement({ owner: "tailnet" })
);

const clientId = Element({
  description:
    "The client ID, as Tailscale's Trust credentials page shows it, such as kAbc1CNTRL.",
  label: "Client ID",
  step: "new",
  type: "string",
});

/** The Tailscale credential the connector uses for a tailnet. Its option is
 * the item's `credential.type`. */
const TailscaleCredential = Element({
  default: "federated",
  description:
    "How the connector authenticates to Tailscale. A federated identity trusts the connector's own cloud identity, so no secret is stored anywhere.",
  label: "Credential",
  options: {
    federated: Option({
      description:
        "A Tailscale federated identity whose issuer and subject are the connector's own cloud identity. Leave its audience unset: P0 uses Tailscale's default.",
      label: "Federated identity",
      maturity: "beta",
      schema: { clientId },
    }),
    "oauth-client": Option({
      description:
        "A Tailscale OAuth client whose secret is in your secret manager, in the connector's cloud. The connector reads the secret itself.",
      label: "OAuth client",
      maturity: "beta",
      schema: {
        clientId,
        vault: TailscaleSecretVault,
        clientSecretName: Element({
          description:
            "The name, not the ARN or the full resource name, of the secret that holds, or will hold, the OAuth client's secret. The tailnet's Terraform lets the connector read exactly this secret.",
          label: "Client secret's secret",
          step: "new",
          type: "string",
        }),
      },
    }),
  },
  step: "new",
  type: "select",
});

/** Each artifact the install produces belongs to one of two items. The
 * deployed connector, its identity and P0's permission to invoke it belong to
 * `connector`. A tailnet's credential, and the connector's read on its
 * secret, belong to `tailnet`. What a request reaches is listed live from the
 * tailnet, so it's no install item. */
export const TailscaleComponents = InstallSpec({
  // One item per deployed connector, keyed by an id the customer chooses. A
  // connector serves every tailnet that points at it.
  //
  // The connector's identity isn't stored: its IAM role, or its service
  // account, is named after the connector (see `toConnectorIdentity`).
  connector: ItemComponent({
    description:
      "Runs P0's Tailscale connector in your AWS account or Google Cloud project. One connector can serve many tailnets, so one is usually enough.",
    label: "Connector",
    maturity: "beta",
    schema: { hosting: ConnectorHosting },
    type: "string",
    validator: async (id) =>
      id.length <= CONNECTOR_ID_MAX_LENGTH && CONNECTOR_ID_PATTERN.test(id)
        ? undefined
        : INVALID_CONNECTOR_ID,
  }),
  // One item per tailnet, keyed by its Tailnet ID.
  //
  // The ID has no item validator here: the install framework's error for one
  // repeats the whole request, which may hold a secret pasted in place of its
  // secret's name. The backend checks it instead (`TAILNET_ID_PATTERN`).
  tailnet: ItemComponent({
    description:
      "A tailnet P0 can grant access in, through one of your connectors. Its ID is the Tailnet ID from Tailscale's Settings, General, such as T1234CNTRL.",
    label: "Tailnet",
    maturity: "beta",
    // The connector and the credential can't change once the item exists:
    // the tailnet's Terraform grants exactly that connector its credential.
    // The form asks for these fields in this order.
    schema: {
      connector: Element({
        description:
          "The connector that acts on this tailnet. P0 lists the Tailscale connectors you've installed.",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      credential: TailscaleCredential,
      discovery: Element({
        description:
          "What P0 found on the tailnet when it last validated it: how many P0 postures its policy file defines.",
        label: "Discovered",
        type: "generated",
      }),
      dnsName: Element({
        description:
          "The tailnet's DNS name, such as tail1234.ts.net, as P0 found it when it last validated the tailnet. Requestors and approvers see it next to the Tailnet ID. Empty when the tailnet has no device of its own.",
        label: "DNS name",
        type: "generated",
      }),
    },
    type: "string",
  }),
});
